import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { IValueMap } from 'mobx-react/src/types/IValueMap';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import BookingsStore from '@portal/env/stores/bookingsStore';
import AuthStore from '@portal/env/stores/authStore';
import { queryKeys } from '@portal/ui/hooks/queryKeys';
import type { MyBookingData } from '@portal/types/nexudus/booking';

export const bookingsQueryKeys = {
  upcomingBookings: [...queryKeys.bookings, 'upcoming'] as const,
  teamBookings: [...queryKeys.bookings, 'team'] as const,
};

export const upcomingBookingsQueryOptions = (
  bookingsStore: BookingsStore,
  authStore: AuthStore,
) =>
  queryOptions({
    queryKey: bookingsQueryKeys.upcomingBookings,
    enabled: authStore.isLoggedIn,
    queryFn: () =>
      bookingsStore.loadUpcomingBookings() as Promise<MyBookingData[]>,
  });

export const teamBookingsQueryOptions = (
  bookingsStore: BookingsStore,
  authStore: AuthStore,
) =>
  queryOptions({
    queryKey: bookingsQueryKeys.teamBookings,
    enabled: authStore.isLoggedIn,
    queryFn: () => bookingsStore.loadTeamBookings() as Promise<MyBookingData[]>,
  });

export const useUpcomingBookingsQuery = () => {
  const stores = useContext(MobXProviderContext) as Record<string, unknown>;
  if (!stores?.bookingsStore || !stores?.authStore) {
    throw new Error(
      'useUpcomingBookingsQuery must be used within a MobX Provider',
    );
  }
  return useSuspenseQuery(
    upcomingBookingsQueryOptions(
      stores.bookingsStore as BookingsStore,
      stores.authStore as AuthStore,
    ),
  );
};

export const useTeamBookingsQuery = () => {
  const stores = useContext(MobXProviderContext) as Record<string, unknown>;
  if (!stores?.bookingsStore || !stores?.authStore) {
    throw new Error('useTeamBookingsQuery must be used within a MobX Provider');
  }
  return useSuspenseQuery(
    teamBookingsQueryOptions(
      stores.bookingsStore as BookingsStore,
      stores.authStore as AuthStore,
    ),
  );
};

/**
 * Hook to access the raw bookings store for actions and observables not covered
 * by the query hooks above (e.g. loadBasketBooking, resources, unpaid bookings).
 *
 * IMPORTANT: Components using this hook must be wrapped with MobX `observer()`
 * to react to observable changes.
 */
export function useBookings() {
  const stores = useContext(MobXProviderContext) as IValueMap;
  if (!stores?.bookingsStore) {
    throw new Error('useBookings must be used within a MobX Provider');
  }
  return { store: stores.bookingsStore as BookingsStore };
}
